import { Language } from '../types';

// Country code to language mapping
const countryLanguageMap: Record<string, Language> = {
  // English speaking countries
  'US': 'en', 'CA': 'en', 'GB': 'en', 'AU': 'en', 'NZ': 'en', 'IE': 'en',
  
  // Spanish speaking countries
  'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'PE': 'es', 'VE': 'es', 
  'CL': 'es', 'EC': 'es', 'GT': 'es', 'CU': 'es', 'BO': 'es', 'DO': 'es',
  'HN': 'es', 'PY': 'es', 'SV': 'es', 'NI': 'es', 'CR': 'es', 'PA': 'es',
  'UY': 'es', 'PR': 'es',
  
  // Chinese speaking regions
  'CN': 'zh', 'TW': 'zh', 'HK': 'zh', 'MO': 'zh', 'SG': 'zh',
  
  // Arabic speaking countries
  'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'IQ': 'ar', 'JO': 'ar', 'KW': 'ar',
  'LB': 'ar', 'LY': 'ar', 'MA': 'ar', 'OM': 'ar', 'QA': 'ar', 'SY': 'ar',
  'TN': 'ar', 'DZ': 'ar', 'BH': 'ar', 'YE': 'ar', 'SD': 'ar',
  
  // Hindi speaking regions
  'IN': 'hi',
  
  // Mongolian speaking countries
  'MN': 'mn',
  
  // Japanese
  'JP': 'ja',
  
  // Korean
  'KR': 'ko',
  
  // Kazakh
  'KZ': 'kk',
  
  // Uzbek
  'UZ': 'uz',
  
  // Kyrgyz
  'KG': 'ky',
  
  // Thai
  'TH': 'th',
  
  // Indonesian
  'ID': 'id',
  
  // Vietnamese
  'VN': 'vi',
  
  // Nepali
  'NP': 'ne',
};

interface GeolocationData {
  country_code: string;
  country: string;
  city?: string;
  region?: string;
}

export const detectLanguageByIP = async (): Promise<Language> => {
  try {
    // Try multiple geolocation services for redundancy.
    // Both return a country_code field (the previous backup, api.ipify.org,
    // only returned the IP itself, so the fallback never actually worked).
    const services = [
      'https://ipapi.co/json/',
      'https://ipwho.is/', // Backup service, no API key required
    ];

    for (const serviceUrl of services) {
      try {
        const response = await fetch(serviceUrl, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
          },
          // Add timeout to prevent hanging
          signal: AbortSignal.timeout(5000), // 5 second timeout
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data: GeolocationData = await response.json();
        
        if (data.country_code) {
          const detectedLanguage = countryLanguageMap[data.country_code.toUpperCase()];
          
          if (detectedLanguage) {
            console.log(`🌍 IP Geolocation: Detected country ${data.country_code} -> Language: ${detectedLanguage}`);
            return detectedLanguage;
          } else {
            console.log(`🌍 IP Geolocation: Country ${data.country_code} not mapped, using Mongolian default`);
          }
        }
        
        // If we get here, the service worked but didn't return useful data
        break;
      } catch (serviceError) {
        console.log(`⚠️ Geolocation service ${serviceUrl} failed:`, serviceError);
        continue; // Try next service
      }
    }
  } catch (error) {
    console.log('⚠️ IP geolocation detection failed:', error);
  }

  // Default to Mongolian as requested
  console.log('🇲🇳 Using Mongolian as default language');
  return 'mn';
};

export const initializeLanguageDetection = async (): Promise<Language> => {
  // First check if user has manually selected a language (stored in localStorage)
  const savedLanguage = localStorage.getItem('i18nextLng') as Language;
  if (savedLanguage && isValidLanguage(savedLanguage)) {
    console.log(`💾 Using saved language preference: ${savedLanguage}`);
    return savedLanguage;
  }

  // Then try IP-based detection
  const ipDetectedLanguage = await detectLanguageByIP();
  
  // Store the detected language for future visits
  localStorage.setItem('i18nextLng', ipDetectedLanguage);
  
  return ipDetectedLanguage;
};

// Helper function to validate if language is supported
const isValidLanguage = (lang: string): lang is Language => {
  const supportedLanguages: Language[] = [
    'en', 'es', 'zh', 'ar', 'hi', 'mn', 'ja', 'ko', 'kk', 'uz', 'ky', 'th', 'id', 'vi', 'ne'
  ];
  return supportedLanguages.includes(lang as Language);
};